/**
 * Custom Search Drawer — Web Component
 * Predictive search via Shopify /search/suggest.json
 */
class CustomSearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.DEBOUNCE_MS = 300;
    this.MAX_RECENT = 6;
    this.STORAGE_KEY = 'zh_recent_searches';
    this.debounceTimer = null;
    this.currentQuery = '';
    this.abortCtrl = null;
    this.focusedIndex = -1;
    this.focusableItems = [];
    this.triggerElement = null;
    this.overlay = null;

    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.closeBtn = this.querySelector('.search-drawer__close');
    this.input = this.querySelector('.search-drawer__input');
    this.form = this.querySelector('.search-drawer__form');
    this.drawerBody = this.querySelector('.search-drawer__body');
    this.loading = this.querySelector('.search-drawer__loading');
    this.resultsWrap = this.querySelector('.search-drawer__results');
    this.noResults = this.querySelector('.search-drawer__no-results');
    this.initialState = this.querySelector('.search-drawer__initial');
    this.clearInputBtn = this.querySelector('.search-drawer__clear');

    this.sections = {
      products: this.querySelector('#SearchDrawerProducts'),
      collections: this.querySelector('#SearchDrawerCollections'),
      articles: this.querySelector('#SearchDrawerArticles'),
      pages: this.querySelector('#SearchDrawerPages')
    };

    this.recentWrap = this.querySelector('#SearchDrawerRecent');
    this.recentList = this.querySelector('#SearchDrawerRecentList');
    this.clearRecentBtn = this.querySelector('#SearchDrawerClearRecent');

    if (!this.input) return;

    /* Prevent re-initialization when moving to body */
    if (this._initialized) return;
    this._initialized = true;

    /* Move drawer to body so it escapes any section stacking context */
    document.body.appendChild(this);

    /* Create overlay element on body */
    this.overlay = document.createElement('div');
    this.overlay.className = 'search-drawer-overlay';
    this.overlay.id = 'SearchDrawerOverlay';
    document.body.insertBefore(this.overlay, this);

    /* Force correct initial visibility */
    this.hide(this.loading);
    this.hide(this.noResults);
    this.hide(this.resultsWrap);
    this.show(this.initialState);
    this.renderRecentSearches();

    this.bindEvents();
    this.initSoundHover();

    /* Shopify Design Mode */
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => {
        document.querySelectorAll('[data-search-drawer-toggle]').forEach((btn) => {
          btn.addEventListener('click', (e) => { e.preventDefault(); this.open(btn); });
        });
      });
    }

    window.SearchDrawer = { open: (trigger) => this.open(trigger), close: () => this.close() };
  }

  bindEvents() {
    /* Trigger buttons */
    document.querySelectorAll('[data-search-drawer-toggle]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(btn);
      });
    });

    /* Close button */
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

    /* Overlay click */
    this.overlay.addEventListener('click', () => this.close());

    /* Search input */
    this.input.addEventListener('input', () => {
      this.toggleClearBtn();
      var q = this.input.value.trim();
      if (q.length < 1) {
        if (this.abortCtrl) this.abortCtrl.abort();
        this.currentQuery = '';
        this.hideAllStates();
        this.show(this.initialState);
        this.renderRecentSearches();
        return;
      }
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => { this.performSearch(q); }, this.DEBOUNCE_MS);
    });

    /* Clear input button */
    if (this.clearInputBtn) {
      this.clearInputBtn.addEventListener('click', () => {
        this.input.value = '';
        this.currentQuery = '';
        this.toggleClearBtn();
        this.hideAllStates();
        this.show(this.initialState);
        this.renderRecentSearches();
        this.input.focus();
      });
    }

    /* Form submit */
    if (this.form) {
      this.form.addEventListener('submit', () => {
        var q = this.input.value.trim();
        if (q.length > 0) this.saveRecentSearch(q);
      });
    }

    /* Clear recent button */
    if (this.clearRecentBtn) this.clearRecentBtn.addEventListener('click', () => this.clearRecentSearches());

    /* Trending buttons */
    this.querySelectorAll('.search-drawer__trending-item').forEach((btn) => {
      btn.addEventListener('click', () => {
        var term = btn.getAttribute('data-search-term');
        if (term) { this.input.value = term; this.toggleClearBtn(); this.performSearch(term); }
      });
    });
  }

  /* ================================================================
     HELPERS: show / hide
     ================================================================ */
  hide(el) {
    if (el) el.setAttribute('hidden', '');
  }

  show(el) {
    if (el) el.removeAttribute('hidden');
  }

  /* ================================================================
     OPEN / CLOSE
     ================================================================ */
  open(trigger) {
    this.triggerElement = trigger || document.activeElement;
    this.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.classList.add('search-drawer-open');

    /* Reset to initial state on every open */
    this.input.value = '';
    this.currentQuery = '';
    this.hideAllStates();
    this.show(this.initialState);
    this.renderRecentSearches();
    this.toggleClearBtn();

    setTimeout(() => { this.input.focus(); }, 120);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  close() {
    this.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.classList.remove('search-drawer-open');
    document.removeEventListener('keydown', this.handleKeyDown);
    if (this.abortCtrl) this.abortCtrl.abort();

    if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
      this.triggerElement.focus();
    }
  }

  hideAllStates() {
    this.hide(this.loading);
    this.hide(this.resultsWrap);
    this.hide(this.noResults);
    this.hide(this.initialState);
    this.removeViewAllLink();
    Object.keys(this.sections).forEach((k) => { this.hide(this.sections[k]); });
  }

  /* ================================================================
     KEYBOARD
     ================================================================ */
  handleKeyDown(e) {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key === 'Tab') { this.trapFocus(e); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.navigateResults(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (e.key === 'Enter' && this.focusedIndex >= 0 && this.focusableItems[this.focusedIndex]) {
      e.preventDefault();
      this.focusableItems[this.focusedIndex].click();
    }
  }

  trapFocus(e) {
    var els = this.querySelectorAll(
      'a[href]:not([hidden]), button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden])'
    );
    if (!els.length) return;
    var first = els[0], last = els[els.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  navigateResults(dir) {
    this.focusableItems = Array.from(
      this.resultsWrap.querySelectorAll('.search-drawer__product-link, .search-drawer__link')
    );
    if (!this.focusableItems.length) return;
    if (this.focusedIndex >= 0 && this.focusableItems[this.focusedIndex]) {
      this.focusableItems[this.focusedIndex].setAttribute('aria-selected', 'false');
    }
    this.focusedIndex += dir;
    if (this.focusedIndex < 0) this.focusedIndex = this.focusableItems.length - 1;
    if (this.focusedIndex >= this.focusableItems.length) this.focusedIndex = 0;
    var item = this.focusableItems[this.focusedIndex];
    item.setAttribute('aria-selected', 'true');
    item.focus();
    item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /* ================================================================
     CLEAR BUTTON
     ================================================================ */
  toggleClearBtn() {
    if (!this.clearInputBtn) return;
    if (this.input.value.length > 0) this.show(this.clearInputBtn);
    else this.hide(this.clearInputBtn);
  }

  /* ================================================================
     FETCH PREDICTIVE SEARCH
     ================================================================ */
  performSearch(query) {
    if (query === this.currentQuery) return;
    this.currentQuery = query;

    if (this.abortCtrl) this.abortCtrl.abort();
    this.abortCtrl = new AbortController();

    /* Show loading, hide everything else */
    this.hideAllStates();
    this.show(this.loading);

    var url = '/search/suggest.json'
      + '?q=' + encodeURIComponent(query)
      + '&resources[type]=product,collection,page,article'
      + '&resources[limit]=6';

    fetch(url, {
      signal: this.abortCtrl.signal,
      headers: { 'Accept': 'application/json' }
    })
    .then((res) => {
      if (!res.ok) throw new Error(res.status);
      return res.json();
    })
    .then((data) => {
      this.hide(this.loading);
      this.renderResults(data, query);
      this.saveRecentSearch(query);
    })
    .catch((err) => {
      if (err.name === 'AbortError') return;
      this.hide(this.loading);
      this.hideAllStates();
      this.show(this.noResults);
    });
  }

  /* ================================================================
     RENDER RESULTS
     ================================================================ */
  renderResults(data, query) {
    this.hideAllStates();

    var resources = data.resources && data.resources.results ? data.resources.results : {};
    var hasAny = false;

    /* Products */
    if (resources.products && resources.products.length) {
      hasAny = true;
      var list = this.sections.products.querySelector('.search-drawer__product-list');
      list.innerHTML = '';
      resources.products.forEach((product) => {
        var li = document.createElement('li');
        li.className = 'search-drawer__product-item';

        var imgHtml;
        if (product.image) {
          imgHtml = '<img class="search-drawer__product-image" src="'
            + this.getSizedImage(product.image, '200x200')
            + '" alt="' + this.esc(product.title) + '" loading="lazy" width="56" height="56">';
        } else {
          imgHtml = '<div class="search-drawer__product-image--placeholder">'
            + '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">'
            + '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>'
            + '<path d="m21 15-5-5L5 21"/></svg></div>';
        }

        li.innerHTML = '<a href="' + product.url + '" class="search-drawer__product-link" role="option" aria-selected="false">'
          + imgHtml
          + '<div class="search-drawer__product-info">'
          + '<p class="search-drawer__product-title">' + this.highlight(product.title, query) + '</p>'
          + '</div></a>';
        list.appendChild(li);
      });
      this.show(this.sections.products);
    }

    /* Collections */
    if (resources.collections && resources.collections.length) {
      hasAny = true;
      this.renderLinkSection(this.sections.collections, resources.collections, query, 'collection');
    }

    /* Articles */
    if (resources.articles && resources.articles.length) {
      hasAny = true;
      this.renderLinkSection(this.sections.articles, resources.articles, query, 'article');
    }

    /* Pages */
    if (resources.pages && resources.pages.length) {
      hasAny = true;
      this.renderLinkSection(this.sections.pages, resources.pages, query, 'page');
    }

    if (hasAny) {
      this.show(this.resultsWrap);
      this.input.setAttribute('aria-expanded', 'true');
      this.addViewAllLink(query);
      this.focusedIndex = -1;
    } else {
      this.show(this.noResults);
      this.input.setAttribute('aria-expanded', 'false');
    }
  }

  renderLinkSection(section, items, query, type) {
    var list = section.querySelector('.search-drawer__link-list');
    list.innerHTML = '';
    var icon = this.linkIcon(type);
    items.forEach((item) => {
      var li = document.createElement('li');
      li.className = 'search-drawer__link-item';
      li.innerHTML = '<a href="' + item.url + '" class="search-drawer__link" role="option" aria-selected="false">'
        + '<span class="search-drawer__link-icon">' + icon + '</span>'
        + '<span>' + this.highlight(item.title, query) + '</span></a>';
      list.appendChild(li);
    });
    this.show(section);
  }

  addViewAllLink(query) {
    this.removeViewAllLink();
    var a = document.createElement('a');
    a.className = 'search-drawer__view-all sound__hover hover--button--active';
    a.href = '/search?q=' + encodeURIComponent(query) + '&type=product,article,page&options[prefix]=last';
    a.textContent = 'View All Results';
    this.resultsWrap.parentNode.insertBefore(a, this.resultsWrap.nextSibling);
    this.bindSoundHover(a);
  }

  removeViewAllLink() {
    var el = this.drawerBody.querySelector('.search-drawer__view-all');
    if (el) el.remove();
  }

  linkIcon(type) {
    if (type === 'collection') return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="1" y="1" width="14" height="14" rx="2"/><path d="M1 6h14M6 1v14"/></svg>';
    if (type === 'article') return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M2 3h12M2 6.5h12M2 10h8M2 13h5"/></svg>';
    if (type === 'page') return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 1h7l3 3v11H3z"/><path d="M10 1v3h3"/></svg>';
    return '';
  }

  /* ================================================================
     RECENT SEARCHES
     ================================================================ */
  getRecent() {
    try { var d = JSON.parse(localStorage.getItem(this.STORAGE_KEY)); return Array.isArray(d) ? d : []; }
    catch (e) { return []; }
  }

  saveRecentSearch(q) {
    if (!q || q.length < 1) return;
    var s = this.getRecent().filter((x) => { return x.toLowerCase() !== q.toLowerCase(); });
    s.unshift(q);
    if (s.length > this.MAX_RECENT) s = s.slice(0, this.MAX_RECENT);
    try { localStorage.setItem(this.STORAGE_KEY, JSON.stringify(s)); } catch (e) {}
  }

  clearRecentSearches() {
    try { localStorage.removeItem(this.STORAGE_KEY); } catch (e) {}
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    if (!this.recentWrap || !this.recentList) return;
    var searches = this.getRecent();
    if (!searches.length) { this.hide(this.recentWrap); return; }

    this.recentList.innerHTML = '';
    searches.forEach((term) => {
      var li = document.createElement('li');
      li.className = 'search-drawer__recent-item';
      li.innerHTML = '<button type="button" class="search-drawer__recent-link" data-search-term="' + this.esc(term) + '">'
        + '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.2"><circle cx="7" cy="7" r="6"/><path d="M7 3.5V7l2.5 1.5"/></svg>'
        + '<span>' + this.esc(term) + '</span></button>';
      this.recentList.appendChild(li);
    });
    this.show(this.recentWrap);

    this.recentList.querySelectorAll('[data-search-term]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.input.value = btn.getAttribute('data-search-term');
        this.toggleClearBtn();
        this.performSearch(this.input.value.trim());
      });
    });
  }

  /* ================================================================
     UTILITIES
     ================================================================ */
  highlight(text, query) {
    if (!query || !text) return this.esc(text);
    var safe = this.esc(text);
    var re = new RegExp('(' + query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'gi');
    return safe.replace(re, '<mark class="search-drawer__highlight">$1</mark>');
  }

  esc(str) {
    if (!str) return '';
    var d = document.createElement('div');
    d.appendChild(document.createTextNode(str));
    return d.innerHTML;
  }

  getSizedImage(url, size) {
    if (!url) return '';
    if (url.indexOf('_' + size) !== -1) return url;
    return url.replace(/(\.[a-z]+)(\?|$)/i, '_' + size + '$1$2');
  }

  /* ================================================================
     SOUND HOVER (for dynamically created .sound__hover elements)
     ================================================================ */
  initSoundHover() {
    if (this._soundInitialized) return;
    this._soundInitialized = true;
    var soundUrl = 'https://cdn.shopify.com/s/files/1/0959/3811/7999/files/KoxnwwEQHSLNC4Eqn4RcMrWefYs_1.mp3?v=1775050744';
    this._sound = new Audio(soundUrl);
    this._audioUnlocked = false;
    this._isPlaying = false;
    var self = this;
    document.addEventListener('click', function () {
      if (self._audioUnlocked) return;
      self._sound.play().then(function () {
        self._sound.pause();
        self._sound.currentTime = 0;
        self._audioUnlocked = true;
      }).catch(function () {});
    }, { once: true });
  }

  bindSoundHover(el) {
    if (!el || el._soundBound) return;
    el._soundBound = true;
    var self = this;
    el.addEventListener('mouseenter', function () {
      if (!self._audioUnlocked || self._isPlaying) return;
      self._isPlaying = true;
      self._sound.currentTime = 0;
      self._sound.play().catch(function () {});
    });
    el.addEventListener('mouseleave', function () {
      self._sound.pause();
      self._sound.currentTime = 0;
      self._isPlaying = false;
    });
  }
}

customElements.define('custom-search-drawer', CustomSearchDrawer);
